import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { IssueReportRecord } from './issueReport.props';

const selectIssueReport = (state: RootState) => state.issueReport;

/** Reports in the order they were filed. */
const selectIssueReports = createSelector(
  selectIssueReport,
  (state): IssueReportRecord[] =>
    state.order.map((id) => state.byId[id]).filter(Boolean)
);

const selectIssueReportById = (issueId: string | null) =>
  createSelector(selectIssueReport, (state) =>
    issueId ? state.byId[issueId] ?? null : null
  );

/** Voided reports are excluded — a retracted flag is not outstanding. */
const selectIssueReportTotalCount = createSelector(
  selectIssueReports,
  (reports) => reports.filter((report) => !report.voided).length
);

/** Reports that reached their terminal state without a type. */
const selectUnclassifiedIssueReportCount = createSelector(
  selectIssueReports,
  (reports) =>
    reports.filter((report) => !report.voided && !report.issue_type).length
);

const selectHasFailedIssueReports = createSelector(
  selectIssueReports,
  (reports) => reports.some((report) => report.status === 'failed')
);

/** The record inside the open flag window — what the panel acts on. */
const selectActiveIssueReport = createSelector(
  selectIssueReport,
  (state): IssueReportRecord | null =>
    state.activeReportId ? state.byId[state.activeReportId] ?? null : null
);

const selectActiveFlagWindowExpiresAt = createSelector(
  selectIssueReport,
  (state) => state.activeWindowExpiresAt
);

/** Banked remainder while paused; null while the countdown is running. */
const selectFlagWindowRemainingMs = createSelector(
  selectIssueReport,
  (state) => state.remainingMs
);

export {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
  selectHasFailedIssueReports,
  selectIssueReport,
  selectIssueReportById,
  selectIssueReportTotalCount,
  selectIssueReports,
  selectFlagWindowRemainingMs,
  selectUnclassifiedIssueReportCount,
};
